import { type JSX } from 'react';

import { type Destination, routes } from '../routes';
import { AppLink } from '../discovery/router';

/**
 * The website pages' footer, built from `JT Site Footer.dc.html`.
 *
 * A new component used only on the website pages; the homepage keeps its own
 * `JTFooter` (owner decision D1, 2026-11-01).
 *
 * Every link goes where the frame sends it, mapped to this site's pages (the
 * frame's Places Near You is `/nearby`, its Understanding Fees is "Delivery
 * information", and so on). A destination with no page at all yet — About,
 * Press, the legal pages, Delivery information — is drawn as the frame draws
 * it but renders as a "coming soon" control rather than a dead link, the same
 * rule the rest of the site follows.
 */

interface FooterLink {
  readonly t: string;
  /** A page on this site, or a destination with no page yet. */
  readonly to: string | Destination;
}

const COLUMNS: readonly { readonly head: string; readonly links: readonly FooterLink[] }[] = [
  {
    head: 'Shop',
    links: [
      { t: 'Prescription medicines', to: routes.nearby.path },
      { t: 'Over the counter', to: routes.nearby.path },
      { t: 'Vitamins & supplements', to: routes.nearby.path },
      { t: 'Health & devices', to: routes.nearby.path },
      { t: 'Health services', to: routes.nearby.path },
    ],
  },
  {
    head: 'Services',
    links: [
      { t: 'Online consultations', to: routes.nearby.path },
      { t: 'Lab tests at home', to: routes.nearby.path },
      { t: 'Prescription service', to: routes.prescriptions.path },
      { t: 'Repeat orders', to: routes.prescriptions.path },
      { t: 'Health support', to: routes.helpCentre.path },
    ],
  },
  {
    head: 'Company',
    links: [
      { t: 'About us', to: routes.about },
      { t: 'Careers', to: routes.couriers.path },
      { t: 'List your business', to: routes.partnersWithUs.path },
      { t: 'Press & media', to: routes.press },
      { t: 'Contact us', to: routes.helpCentre.path },
    ],
  },
  {
    head: 'Help',
    links: [
      { t: 'Help centre', to: routes.helpCentre.path },
      { t: 'Delivery information', to: routes.deliveryInformation },
      { t: 'Returns & refunds', to: routes.helpCentre.path },
      { t: 'Privacy policy', to: routes.privacy },
      { t: 'Terms & conditions', to: routes.terms },
    ],
  },
];

/** A footer link: a real page, or an honest "coming soon" control. */
function FooterItem({
  link,
  className,
}: {
  readonly link: FooterLink;
  readonly className: string;
}): JSX.Element {
  if (typeof link.to === 'string') {
    return (
      <AppLink to={link.to} className={className}>
        {link.t}
      </AppLink>
    );
  }
  return <SoonLink label={link.t} className={className} />;
}

/** Drawn like a link, refuses activation, and says why. Same rule as `DestinationLink`. */
export function SoonLink({
  label,
  className,
}: {
  readonly label: string;
  readonly className: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`${className} jt-site-soon`}
      aria-disabled="true"
      title={`${label} — coming soon`}
      onClick={(event) => event.preventDefault()}
    >
      {label}
      <span className="jt-visually-hidden"> (coming soon)</span>
    </button>
  );
}

export function SiteFooter(): JSX.Element {
  const year = new Date().getFullYear();

  return (
    <footer className="jt-site-footer">
      <div className="jt-site-footer__grid">
        <div className="jt-site-footer__brand">
          <span className="jt-site-footer__word">Just Treated</span>
          <p className="jt-site-footer__blurb">
            Your trusted healthcare platform for medicines, consultations, lab tests and more.
          </p>
        </div>
        {COLUMNS.map((column) => (
          <nav key={column.head} className="jt-site-footer__col" aria-label={column.head}>
            <span className="jt-site-footer__head">{column.head}</span>
            {column.links.map((link) => (
              <FooterItem key={link.t} link={link} className="jt-site-footer__link" />
            ))}
          </nav>
        ))}
      </div>
      <div className="jt-site-footer__bar">
        <div className="jt-site-footer__reg">
          <span>Regulated by</span>
          <a
            href="https://www.cqc.org.uk/location/1-26088938261"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View our Care Quality Commission rating (opens in new tab)"
            className="jt-site-footer__cqc"
          >
            <img
              src="/home/footer/cqc-logo.webp"
              alt="Care Quality Commission"
              width="72"
              height="54"
              loading="lazy"
            />
          </a>
        </div>
        <span className="jt-site-footer__legal">
          © {year} Just Treated. All rights reserved. GPhC-registered providers.
        </span>
        <div className="jt-site-footer__end">
          <SoonLink label="Privacy Policy" className="jt-site-footer__link" />
          <SoonLink label="Terms & Conditions" className="jt-site-footer__link" />
          <span>🇬🇧 UK</span>
        </div>
      </div>
    </footer>
  );
}
